import React from 'react';
import type { SmartRules, Rule, StageOption, PostStageAction, WorkflowContext } from './types';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  CheckCircle,
  XCircle,
  Plus,
  Edit2,
  Trash2,
  ChevronUp,
  ChevronDown,
  Zap,
} from 'lucide-react';
import { getRuleConditionSummary, getRuleActionSummary } from './utils';
import { Badge } from '../ui/badge';
import {
  ApprovalCard,
  ApprovalCardHeader,
  ApprovalCardTitle,
  ApprovalCardDescription,
  ApprovalCardContent,
  ApprovalButton,
  ApprovalIconButton,
  ApprovalEmptyState,
} from '../shared/approval';

interface Step3SmartRulesProps {
  data: SmartRules;
  onChange: (data: SmartRules) => void;
  availableStages: StageOption[];
  workflowContext: WorkflowContext;
  onAddRule: (executionTiming: 'pre-approval' | 'post-approval', context?: 'approved' | 'rejected') => void;
  onEditRule: (rule: Rule) => void;
  onDeleteRule: (ruleId: string) => void;
  onReorderRule: (ruleId: string, direction: 'up' | 'down') => void;
}

export const Step3SmartRules: React.FC<Step3SmartRulesProps> = ({
  data,
  onChange,
  availableStages,
  workflowContext,
  onAddRule,
  onEditRule,
  onDeleteRule,
  onReorderRule,
}) => {
  const [activeTab, setActiveTab] = React.useState('routing');


  // Filter rules by execution timing
  const rules = data.rules || [];
  const preApprovalRules = rules.filter((r) => r.executionTiming === 'pre-approval');
  const postApprovalRulesApproved = rules.filter(
    (r) => r.executionTiming === 'post-approval' && r.context === 'approved'
  );
  const postApprovalRulesRejected = rules.filter(
    (r) => r.executionTiming === 'post-approval' && r.context === 'rejected'
  );

  // Handlers for approved actions
  const handleApprovedActionChange = (type: string) => {
    onChange({
      ...data,
      onApproved: {
        ...data.onApproved,
        type: type as any,
        targetStageId: undefined,
        targetStageName: undefined,
      },
    });
  };

  const handleApprovedStageSelect = (stageId: string) => {
    const stage = availableStages.find((s) => s.id === stageId);
    onChange({
      ...data,
      onApproved: {
        ...data.onApproved,
        targetStageId: stageId,
        targetStageName: stage?.name,
      },
    });
  };

  // Handlers for rejected actions
  const handleRejectedActionChange = (type: string) => {
    onChange({
      ...data,
      onRejected: {
        ...data.onRejected,
        type: type as any,
        targetStageId: undefined,
        targetStageName: undefined,
      },
    });
  };

  const renderRuleCard = (rule: Rule, index: number, totalRules: number) => {
    const timingBadge =
      rule.executionTiming === 'pre-approval' ? (
        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
          Pre
        </Badge>
      ) : (
        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">
          Post
        </Badge>
      );

    return (
      <ApprovalCard 
        key={rule.id} 
        variant="elevated" 
        hover 
        className="group cursor-pointer transition-all hover:shadow-md"
        onClick={() => onEditRule(rule)}
      >
        <ApprovalCardContent className="p-3">
          <div className="flex items-start space-x-3">
            {/* Rule Number Badge */}
            <div className="flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
                {index + 1}
              </div>
            </div>

            {/* Rule Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-semibold text-sm text-foreground">{rule.name}</h4>
                {timingBadge}
              </div>

              <div className="space-y-1 text-xs">
                {/* Condition - displayed as natural language */}
                <div className="flex items-start space-x-1.5">
                  <span className="text-muted-foreground flex-shrink-0 text-[10px]">📋</span>
                  <span className="text-foreground text-[11px]">
                    {getRuleConditionSummary(rule.condition)}
                  </span>
                </div>

                {/* Action - displayed as natural language */}
                <div className="flex items-start space-x-1.5">
                  <span className="text-muted-foreground flex-shrink-0 text-[10px]">→</span>
                  <span className="text-primary font-medium text-[11px]">{getRuleActionSummary(rule.ifTrue)}</span>
                </div>

                {/* Optional message */}
                {rule.ifTrue.message && (
                  <div className="flex items-start space-x-1.5">
                    <span className="text-muted-foreground flex-shrink-0 text-[10px]">💬</span>
                    <span className="text-[10px] text-muted-foreground italic">"{rule.ifTrue.message}"</span>
                  </div>
                )}
              </div>
              
              {/* Click to edit hint */}
              <div className="mt-2 pt-2 border-t border-border/50">
                <p className="text-[10px] text-muted-foreground flex items-center space-x-1">
                  <Edit2 className="w-3 h-3" />
                  <span>Click to view/edit rule details</span>
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
              {/* Reorder Buttons */}
              <div className="flex flex-col">
                <ApprovalIconButton
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReorderRule(rule.id, 'up');
                  }}
                  disabled={index === 0}
                  className="h-5 w-5 p-0"
                >
                  <ChevronUp className="w-3 h-3" />
                </ApprovalIconButton>
                <ApprovalIconButton
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReorderRule(rule.id, 'down');
                  }}
                  disabled={index === totalRules - 1}
                  className="h-5 w-5 p-0"
                >
                  <ChevronDown className="w-3 h-3" />
                </ApprovalIconButton>
              </div>

              {/* Edit Button */}
              <ApprovalIconButton 
                variant="ghost" 
                size="sm" 
                onClick={(e) => {
                  e.stopPropagation();
                  onEditRule(rule);
                }} 
                className="h-6 w-6 p-0"
              >
                <Edit2 className="w-3 h-3" />
              </ApprovalIconButton>

              {/* Delete Button */}
              <ApprovalIconButton
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteRule(rule.id);
                }}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
              >
                <Trash2 className="w-3 h-3" />
              </ApprovalIconButton>
            </div>
          </div>
        </ApprovalCardContent>
      </ApprovalCard>
    );
  };

  // Safety checks
  if (!workflowContext || !data) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Compact Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">Smart Rules</h2>
            <p className="text-sm text-muted-foreground">
              Stage {workflowContext.currentStageNumber || 1} of {workflowContext.totalStages || 1}: {workflowContext.currentStageName || 'Stage'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabbed Content */}
      <div className="flex-1 min-h-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="grid w-full grid-cols-3 mb-3">
            <TabsTrigger value="routing" className="text-xs">
              <Zap className="w-3 h-3 mr-1" />
              Routing Rules
              {rules.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                  {rules.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              When Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="text-xs">
              <XCircle className="w-3 h-3 mr-1" />
              When Rejected
            </TabsTrigger>
          </TabsList>

          {/* Routing Rules Tab */}
          <TabsContent value="routing" className="flex-1 overflow-y-auto mt-0 space-y-3">
            <ApprovalCard variant="elevated">
              <ApprovalCardHeader className="pb-3">
                <div className="space-y-3">
                  <div>
                    <ApprovalCardTitle className="text-base">Routing Rules</ApprovalCardTitle>
                    <ApprovalCardDescription className="text-xs">
                      Control workflow routing based on conditions
                    </ApprovalCardDescription>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {/* Pre-Approval Button */}
                    <button
                      onClick={() => onAddRule('pre-approval')}
                      className="flex flex-col items-start p-3 bg-card rounded-lg border-2 border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-left group"
                    >
                      <div className="flex items-center space-x-1.5 mb-1">
                        <Plus className="w-3 h-3 text-blue-400" />
                        <span className="text-base">🔵</span>
                        <span className="font-semibold text-foreground text-xs">Pre-Approval Rule</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Executes before approval decision
                      </p>
                    </button>

                    {/* Post-Approval Button */}
                    <button
                      onClick={() => onAddRule('post-approval', 'approved')}
                      className="flex flex-col items-start p-3 bg-card rounded-lg border-2 border-green-500/30 hover:border-green-500/50 hover:bg-green-500/5 transition-all text-left group"
                    >
                      <div className="flex items-center space-x-1.5 mb-1">
                        <Plus className="w-3 h-3 text-green-400" />
                        <span className="text-base">🟢</span>
                        <span className="font-semibold text-foreground text-xs">Post-Approval Rule</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Executes after approval decision
                      </p>
                    </button>
                  </div>
                </div>
              </ApprovalCardHeader>

              <ApprovalCardContent className="pt-0">
                {rules.length === 0 ? (
                  <ApprovalEmptyState
                    icon={Zap}
                    title="No routing rules"
                    description="Add rules to automate workflow routing"
                  />
                ) : (
                  <div className="space-y-2">
                    {rules.map((rule, index) => renderRuleCard(rule, index, rules.length))}
                  </div>
                )}
              </ApprovalCardContent>
            </ApprovalCard>
          </TabsContent>

          {/* When Approved Tab */}
          <TabsContent value="approved" className="flex-1 overflow-y-auto mt-0">
            <ApprovalCard variant="outlined" className="border-l-4 border-accent-success-500 bg-accent-success-500/5">
              <ApprovalCardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-accent-success-500" />
                  <ApprovalCardTitle className="text-accent-success-600 text-base">When Approved</ApprovalCardTitle>
                </div>
                <ApprovalCardDescription className="text-xs">
                  What happens when this stage is approved?
                </ApprovalCardDescription>
              </ApprovalCardHeader>

              <ApprovalCardContent className="pt-0">
                <RadioGroup value={data.onApproved.type} onValueChange={handleApprovedActionChange}>
                  <div className="space-y-2">
                    {/* Continue to next stage */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-success-500/30 hover:border-accent-success-500/50 transition-colors">
                      <RadioGroupItem value="continue" id="approved-continue" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="approved-continue" className="font-medium cursor-pointer text-sm">
                          Continue to next stage
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Move to the next stage in sequence</p>
                      </div>
                    </div>

                    {/* Jump to specific stage */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-success-500/30 hover:border-accent-success-500/50 transition-colors">
                      <RadioGroupItem value="gotoStage" id="approved-goto" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="approved-goto" className="font-medium cursor-pointer text-sm">
                          Jump to specific stage
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Skip stages and go to a specific stage</p>

                        {data.onApproved.type === 'gotoStage' && (
                          <div className="mt-2">
                            <Select value={data.onApproved.targetStageId} onValueChange={handleApprovedStageSelect}>
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder="Select stage..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableStages.map((stage) => (
                                  <SelectItem key={stage.id} value={stage.id} className="text-xs">
                                    {stage.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fully approve */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-success-500/30 hover:border-accent-success-500/50 transition-colors">
                      <RadioGroupItem value="gotoEnd" id="approved-end" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="approved-end" className="font-medium cursor-pointer text-sm">
                          Fully approve (end workflow)
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Mark as approved, skip remaining stages
                        </p>
                      </div>
                    </div>
                  </div>
                </RadioGroup>

                {/* Info about post-approval rules */}
                {postApprovalRulesApproved.length > 0 && (
                  <div className="mt-3 p-3 bg-accent-info-500/10 border border-accent-info-500/30 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <Zap className="w-4 h-4 text-accent-info-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-accent-info-700">
                          Post-approval rules will be evaluated automatically
                        </p>
                        <p className="text-xs text-accent-info-600 mt-1">
                          {postApprovalRulesApproved.length} rule(s) configured for approved context. These rules will be evaluated after the default action above.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </ApprovalCardContent>
            </ApprovalCard>
          </TabsContent>

          {/* When Rejected Tab */}
          <TabsContent value="rejected" className="flex-1 overflow-y-auto mt-0">
            <ApprovalCard variant="outlined" className="border-l-4 border-accent-destructive-500 bg-accent-destructive-500/5">
              <ApprovalCardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <XCircle className="w-5 h-5 text-destructive" />
                  <ApprovalCardTitle className="text-destructive text-base">When Rejected</ApprovalCardTitle>
                </div>
                <ApprovalCardDescription className="text-xs">
                  What happens when this stage is rejected?
                </ApprovalCardDescription>
              </ApprovalCardHeader>

              <ApprovalCardContent className="pt-0">
                <RadioGroup value={data.onRejected.type} onValueChange={handleRejectedActionChange}>
                  <div className="space-y-2">
                    {/* Return to employee */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-destructive-500/30 hover:border-accent-destructive-500/50 transition-colors">
                      <RadioGroupItem value="reject" id="rejected-employee" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="rejected-employee" className="font-medium cursor-pointer text-sm">
                          Return to employee
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Send back for revision</p>
                      </div>
                    </div>

                    {/* Return to previous stage */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-destructive-500/30 hover:border-accent-destructive-500/50 transition-colors">
                      <RadioGroupItem value="gotoStage" id="rejected-previous" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="rejected-previous" className="font-medium cursor-pointer text-sm">
                          Return to previous stage
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Go back one stage</p>
                      </div>
                    </div>

                    {/* End workflow as rejected */}
                    <div className="flex items-start space-x-2 p-2 bg-card rounded-lg border border-accent-destructive-500/30 hover:border-accent-destructive-500/50 transition-colors">
                      <RadioGroupItem value="gotoEnd" id="rejected-end" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="rejected-end" className="font-medium cursor-pointer text-sm">
                          End workflow as rejected
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Reject the entire timesheet</p>
                      </div>
                    </div>
                  </div>
                </RadioGroup>
              </ApprovalCardContent>
            </ApprovalCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
